import { GoiLogo, call } from "goi_common";
import { GOI_PHONE, GOI_PHONE_HREF } from "@/constants/contact";
import { HeaderNav } from "@/components/HeaderNav";
import { PhoneIcon } from "@/components/PhoneIcon";

interface ParlorHeaderProps {
  /**
   * "지금 바로 전화주세요" 띠지 숨김 여부.
   * 광고 LP(/place/[id]/quote) 같이 자체 CTA가 강한 페이지에서 사용.
   * 기본값 false: 기존 페이지(/place/[id]/family/main, /directions/[id])는 영향 없음.
   */
  hidePhoneStrip?: boolean;
  /**
   * 헤더 우측에 "24시간 전화" CTA 버튼 표시 여부.
   * 광고 LP(/quote, /place/[id]/quote)에서 사용. 기본값 false.
   */
  showPhoneCta?: boolean;
  /**
   * 헤더 전화 버튼 클릭 핸들러. customEvent + call을 묶어 호출.
   * 미지정 시 단순 call(GOI_PHONE)만 호출 (goi_call 자동 발화).
   */
  onPhoneCtaClick?: () => void;
  /**
   * "지금 바로 전화주세요" 띠지 클릭 핸들러.
   * 미지정 시 기존대로 tel: 링크가 그대로 동작한다(프롭을 안 넘긴 페이지는 영향 없음).
   * 넘기면 기본 동작을 막고 이 핸들러만 실행한다. 화장장 상세가 문의 모달을 띄울 때 쓴다.
   */
  onPhoneStripClick?: () => void;
  /**
   * Nav 행 표시 여부. 기본 true.
   * 광고 LP(/quote, /place/[id]/quote)는 false로 끈다. 유료 유입을 다른 표면으로
   * 새게 하지 않는 것이 LP의 목적이다.
   */
  showNav?: boolean;
  /**
   * 띠지를 헤더 아래에 겹쳐 띄운다. 본문 흐름에서 빠져 띠지가 나타나고 사라질 때 본문이 밀리지 않는다.
   * 첫 화면 임종 배너가 사라진 뒤에만 띠지를 띄우는 페이지가 쓴다. 본문이 밀리면 배너가 다시 보여 띠지가 깜빡인다
   */
  floatPhoneStrip?: boolean;
  /** 띠지 왼쪽 문구. 임종 배너 뒤에 뜨는 띠지는 배너와 같은 "임종 접수" 로 무엇을 받는 번호인지 밝힌다 */
  phoneStripLabel?: string;
}

export const ParlorHeader = ({
  hidePhoneStrip = false,
  showPhoneCta = false,
  onPhoneCtaClick,
  onPhoneStripClick,
  showNav = true,
  floatPhoneStrip = false,
  phoneStripLabel = "지금 바로 전화주세요",
}: ParlorHeaderProps = {}) => {
  const handlePhoneCtaClick = onPhoneCtaClick ?? (() => call(GOI_PHONE));
  return (
    <header className="pd-header">
      <div className="pd-header__inner">
        <a className="pd-header__logo" href="/" aria-label="고이 홈">
          <GoiLogo size={28} color="var(--goi-ink-900)" />
          <span className="pd-header__logo-divider" aria-hidden="true" />
          <span className="pd-header__logo-label">장례식장 정보</span>
        </a>
        {showPhoneCta && (
          <button
            type="button"
            onClick={handlePhoneCtaClick}
            className="pd-header__phone-cta"
            aria-label={`24시간 전화 ${GOI_PHONE}`}
          >
            <PhoneIcon size={14} />
            <span>24시간 전화</span>
          </button>
        )}
      </div>

      {showNav && <HeaderNav />}

      {!hidePhoneStrip && (
        <div
          className={`pd-header__strip${
            floatPhoneStrip ? " pd-header__strip--float" : ""
          }`}
        >
          <a
            href={GOI_PHONE_HREF}
            className="pd-header__strip-inner"
            onClick={
              onPhoneStripClick &&
              ((event) => {
                event.preventDefault();
                onPhoneStripClick();
              })
            }
          >
            <span className="pd-header__strip-label">{phoneStripLabel}</span>
            <span className="pd-header__strip-divider" aria-hidden="true" />
            <span className="pd-header__strip-phone">
              <PhoneIcon />
              {GOI_PHONE}
            </span>
            <span className="pd-header__strip-brand">고이장례연구소</span>
          </a>
        </div>
      )}
    </header>
  );
};
